import { Stack, useMantineTheme } from '@mantine/core';
import type { JSX, ReactNode } from 'react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ISorted } from '@feathr/components';
import { Alert, Heading, Skeleton, Table } from '@feathr/components';

import type { TDateRange } from '../ReportsConversionsDashboardPage.utils';
import { buildGoalColumns } from './columns';
import { useGoalRows } from './ConversionsByGoalTable.hooks';
import { type IGoalRow, sortGoalRows } from './ConversionsByGoalTable.utils';

interface IProps {
  range: TDateRange;
}

const DEFAULT_SORT: ISorted[] = [{ id: 'numConversions', desc: true }];

function ConversionsByGoalTable({ range }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { rows, isLoading, hasError } = useGoalRows({ range });
  const [sort, setSort] = useState<ISorted[]>(DEFAULT_SORT);

  const visibleRows = useMemo(() => sortGoalRows(rows, sort), [rows, sort]);
  const columns = useMemo(() => buildGoalColumns({ t }), [t]);

  let body: ReactNode;
  if (hasError) {
    body = (
      <Alert title={t("We couldn't load this section. Try again in a moment.")} type={'danger'} />
    );
  } else if (isLoading) {
    body = <Skeleton height={240} />;
  } else {
    body = (
      <Table<IGoalRow>
        columns={columns}
        idKey={'goalId'}
        items={visibleRows}
        noDataText={t('No conversions in this timeframe.')}
        onSortChange={setSort}
        sort={sort}
      />
    );
  }

  return (
    <Stack gap={theme.spacing['3']}>
      <Heading order={2} variant={'h5'}>
        {t('Campaign conversions by goal')}
      </Heading>
      {body}
    </Stack>
  );
}

export default ConversionsByGoalTable;
